import { ContextMenu, ContextMenuContent, ContextMenuTrigger } from '@/components/ui/context-menu';
import { RemoveFromFavorites } from '@/components/menu-items/node-menu-items';
import { MenuProvider, contextPrimitives } from '@/components/menu-renderer';
import { useDamNavigation } from '@/hooks/useDamNavigation';
import { isChildNodeType } from '@/lib/child-node';
import { NodeChip } from '@/components/node-chip';
import { type ChildNodeType } from '@/types/tree';
import { type Favorite } from '@/types/home';

/** Narrows the whole favorite, so `favorite.type` is a ChildNodeType inside the map below. */
const isNavigableFavorite = (favorite: Favorite): favorite is Favorite & { type: ChildNodeType } =>
  isChildNodeType(favorite.type);

export const Favorites = ({ favorites }: { favorites: Array<Favorite> }) => {
  const { navigateToNode } = useDamNavigation();

  return (
    <div className="flex flex-wrap gap-2">
      {/*
        Only containers get a chip. An asset favorite has no browse destination — the click handler
        below already refused to navigate for one — and it used to render anyway, as a chip labelled
        and coloured as a folder that did nothing when clicked.
      */}
      {favorites.filter(isNavigableFavorite).map((favorite) => (
        <ContextMenu key={favorite.node.id}>
          <ContextMenuTrigger
            asChild
            onClick={() => {
              const pathArray = [...favorite.node.ancestors.map((ancestor) => ancestor.slug), favorite.node.slug];

              // Both kinds go through the one navigation entry point. The album branch used to call
              // browseRedirection directly and so skipped the deselect, select-all and
              // locateAssetId resets that folders got.
              void navigateToNode(favorite.node.id, favorite.node.name, favorite.type, pathArray);
            }}
          >
            <NodeChip name={favorite.node.name} nodeType={favorite.type} dataCy={`favorite-chip-${favorite.node.id}`} />
          </ContextMenuTrigger>
          <ContextMenuContent>
            <MenuProvider primitives={contextPrimitives}>
              <RemoveFromFavorites node={favorite} />
            </MenuProvider>
          </ContextMenuContent>
        </ContextMenu>
      ))}
    </div>
  );
};
